import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Send, FileText, FileWarning } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BRANDS = [["univicoustic", "UniVicoustic"], ["unitile", "Unitile"], ["uflex", "U-Flex"]];

/** Marketing / managers: email selected catalogue PDFs to the chosen leads (uses the Lead Desk template). */
export function SendCataloguesDialog({ open, onOpenChange, leadIds, onDone }) {
  const [brand, setBrand] = useState("univicoustic");
  const [picked, setPicked] = useState([]);
  const [eventName, setEventName] = useState("");
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["ld-catalogues", brand], queryFn: async () => (await api.get("/lead-desk/catalogues", { params: { brand } })).data.catalogues, enabled: open });
  const cats = data || [];
  const groups = cats.reduce((acc, c) => { (acc[c.group || "Catalogues"] ||= []).push(c); return acc; }, {});
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const send = async () => {
    if (!picked.length) { toast.error("Pick at least one catalogue"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post("/lead-desk/send-catalogues", { lead_ids: leadIds, catalogue_ids: picked, event_name: eventName.trim() });
      const parts = [`Sent to ${r.sent.length}`];
      if (r.failed.length) parts.push(`${r.failed.length} failed`);
      if (r.skipped_no_email.length) parts.push(`${r.skipped_no_email.length} without email`);
      (r.failed.length && !r.sent.length ? toast.error : toast.success)(parts.join(" · "), { description: r.failed[0] || undefined });
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="send-catalogues-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Send className="h-4 w-4 text-brand" /> Send catalogues to {leadIds.length} lead{leadIds.length === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Each lead with an email gets the Lead Desk catalogue email with the selected PDFs attached; you are in CC / reply-to. Logged on every lead.</DialogDescription>
        </DialogHeader>
        <Tabs value={brand} onValueChange={(b) => { setBrand(b); setPicked([]); }}>
          <TabsList className="h-8">{BRANDS.map(([id, l]) => <TabsTrigger key={id} value={id} className="text-xs h-7" data-testid={`send-brand-${id}`}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
        <div className="space-y-3" data-testid="send-catalogue-list">
          {Object.entries(groups).map(([g, items]) => (
            <div key={g}>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">{g}</p>
              <div className="flex flex-wrap gap-1.5">
                {items.map((c) => (
                  <button key={c.id} type="button" onClick={() => toggle(c.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 h-8 text-xs transition-colors ${picked.includes(c.id) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}
                    data-testid={`send-cat-${c.id}`}>
                    {c.has_file ? <FileText className="h-3 w-3" /> : <FileWarning className="h-3 w-3 text-amber-400" />}{c.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!cats.length && <p className="text-xs text-slate-400">No catalogues for this brand yet.</p>}
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] text-slate-500">Event / context for the message (optional — otherwise each lead's own exhibition is used)</Label>
          <Input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. ACETECH Mumbai 2026" className="h-9" data-testid="send-event-name" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={busy || !picked.length} className="gap-2 bg-slate-900 hover:bg-slate-800" data-testid="send-catalogues-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send {picked.length} PDF{picked.length === 1 ? "" : "s"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
